import { Link } from "react-router-dom";
import { Search, DollarSign, ShieldCheck, Smartphone, FileText, Gauge, ArrowRight } from "lucide-react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/brand";

const HERO = BRAND.heroDually;

export default function ForCarriers() {
  return (
    <PublicLayout>
      <section className="relative min-h-[60vh] flex items-center grain">
        <img src={HERO} alt="Hotshot truck" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        <div className="relative max-w-7xl mx-auto px-6 w-full">
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-xd-orange">For Carriers</span>
          <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-black tracking-tighter max-w-2xl">Run your hotshot business from your phone</h1>
          <p className="mt-5 text-lg text-xd-muted max-w-xl">Get XD Verified, get AI-matched to freight, request dispatch, upload PODs, and track your money — all in one platform built for owner-operators.</p>
          <Button asChild data-testid="carriers-join-btn" className="mt-8 h-13 min-h-[52px] px-8 bg-xd-orange hover:bg-xd-orangeH text-white font-bold">
            <Link to="/register">Become an XD Verified Carrier <ArrowRight size={18} className="ml-2" /></Link>
          </Button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24 grid gap-6 md:grid-cols-3">
        <Feature icon={Search} title="Find & Match Loads" desc="Filter freight by lane, trailer, weight, rate & deadhead. Save it, bid, or request dispatch — rate-per-mile up front." />
        <Feature icon={ShieldCheck} title="XD Verified Status" desc="Complete onboarding, upload docs, and get admin-verified to unlock full carrier tools." />
        <Feature icon={Gauge} title="XD Score" desc="An internal platform-readiness score across authority, insurance, equipment & more." />
        <Feature icon={DollarSign} title="XD Money" desc="Track revenue, rate-per-mile, fuel, expenses & estimated profit in one dashboard." />
        <Feature icon={FileText} title="Document Vault" desc="W-9, COI, BOL, POD, authority docs — organized, secure, and always accessible." />
        <Feature icon={Smartphone} title="Mobile-First" desc="Big buttons, bottom nav, and notifications built for drivers on the road." />
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-8">
        <div className="rounded-2xl border border-xd-gold/40 bg-gradient-to-r from-xd-gold/10 via-xd-paper to-xd-paper p-8 sm:p-10">
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Build the business behind the truck.</h2>
          <p className="mt-3 text-xd-muted max-w-2xl">Running a transportation company takes more than finding freight. Explore marketplace resources designed to help strengthen and grow your business.</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Button asChild data-testid="carriers-business-services" className="h-12 px-6 bg-xd-blue hover:bg-xd-blueH text-white font-bold rounded-xl"><Link to="/marketplace">Explore Business Services</Link></Button>
            <div className="flex items-center text-sm text-xd-muted">Featured partner: <span className="ml-1 text-white font-semibold">Millionaire Mindsetters Foundation</span> — Business Growth & Funding Readiness</div>
          </div>
        </div>
      </section>

      <section className="border-t border-xd-border bg-xd-paper">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <h2 className="font-heading text-4xl font-bold tracking-tight">Ready to roll with XD?</h2>
          <Button asChild data-testid="carriers-cta-btn" className="mt-8 h-13 min-h-[52px] px-8 bg-xd-orange hover:bg-xd-orangeH text-white font-bold">
            <Link to="/register">Join XD FREIGHT</Link>
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="bg-xd-paper border border-xd-border rounded-xl p-6 hover:border-xd-orange/50 transition-colors">
      <span className="grid place-items-center w-12 h-12 rounded-lg bg-xd-orange/15 text-xd-orange"><Icon size={24} /></span>
      <h3 className="mt-4 font-heading text-xl font-bold">{title}</h3>
      <p className="mt-2 text-sm text-xd-muted">{desc}</p>
    </div>
  );
}
